import { Box, Text, Flex, FormatNumber } from '@chakra-ui/react';
import { GroupIcon, Avatar, toaster } from '@halo/ui-chat-react';
import { isEmpty, take, size, gt, eq } from 'lodash/fp';
import { GroupMemberJoinMode } from '@halo/chat-sdk-react';
import { useActionsStore } from '@/store/actions';
import type { GroupItemType, SearchResults } from './helpers';
import { SearchTabs } from './helpers';

export default function ResultsByGroup({ searchResults }: { searchResults?: GroupItemType[] }) {
  return (
    <Box className="List" bg="bg" p="1" borderRadius="lg">
      {searchResults?.map((item, index) => (
        <ResultGroupItem
          key={item.group_id}
          item={item}
          hiddenBorder={eq(size(searchResults), index + 1)}
        />
      ))}
    </Box>
  );
}

export function ResultGroupItem(props: { item: GroupItemType; hiddenBorder?: boolean }) {
  const { t } = useTranslation();
  const { item, hiddenBorder } = props;
  const { setGlobalSearchIsOpen } = useActionsStore();
  const fetchProfileGroup = useActionsStore((state) => state.fetchProfileGroup);

  return (
    <Flex
      gap="2.5"
      align="center"
      px="2"
      pt="2"
      w="full"
      overflow="hidden"
      cursor="pointer"
      _hover={{
        bg: 'bg.subtle',
      }}
      onClick={async () => {
        const result = await fetchProfileGroup(item.gid, GroupMemberJoinMode.Search, true);
        if (!result) {
          toaster.error({ title: t('tips.groupDisbanded') });
          return;
        }
        setGlobalSearchIsOpen(false);
      }}
    >
      <Avatar flex="none" src={item.avatar} size="sm" cursor="pointer" />
      <Box
        flex="1"
        borderBottom={hiddenBorder ? 'none' : '1px solid'}
        borderColor="border.subtle"
        overflow="hidden"
        pb={2}
        {...props}
      >
        <Flex align="center" justify="space-between" gap="2.5">
          <Box overflow="hidden">
            <Flex w="full" gap={1} alignItems="center">
              <GroupIcon style={{ flex: 'none' }} />
              <Box
                flex="1"
                color="fg"
                fontSize="sm"
                fontWeight="semibold"
                lineHeight="normal"
                truncate
                title={item.group_name}
              >
                {item.group_name}
              </Box>
            </Flex>
            <Flex w="full">
              <Text
                fontSize="xs"
                fontWeight="medium"
                lineHeight="normal"
                color="fg.muted"
                truncate
              >
                <FormatNumber value={item.member_num} /> {t('filter.membersOther')}
              </Text>
            </Flex>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
}

// in global tab
export function ResultsGroups({ searchResults }: { searchResults: SearchResults }) {
  const { t } = useTranslation();
  const { groupsData, globalGroupsData } = searchResults;

  return (
    <>
      {!isEmpty(groupsData) && (
        <Box>
          <Text fontSize="xs" color="fg.muted" fontWeight="medium" p="2.5">
            {t('filter.groupsInContacts')}
          </Text>
          <ResultsByGroup searchResults={groupsData} />
        </Box>
      )}
      {!isEmpty(globalGroupsData) && (
        <Box>
          <Text fontSize="xs" color="fg.muted" fontWeight="medium" p="2.5">
            {t('filter.groupsNotInContacts')}
          </Text>
          <ResultsByGroup searchResults={globalGroupsData} />
        </Box>
      )}
    </>
  );
}

// in global tab
export function ResultsTopGroups({
  searchResults,
  setActive,
}: {
  searchResults: SearchResults;
  setActive: (val: SearchTabs) => void;
}) {
  const { t } = useTranslation();
  const { groupsData, globalGroupsData } = searchResults;

  return (
    <>
      {!isEmpty(groupsData) && (
        <Box>
          <Flex alignItems={'center'} justifyContent={'space-between'}>
            <Text fontSize="xs" color="fg.muted" fontWeight="medium" p="2.5">
              {t('filter.groupsInContacts')}
            </Text>

            {gt(size(groupsData), 3) && (
              <Text
                cursor="pointer"
                onClick={() => {
                  setActive(SearchTabs.Groups);
                }}
                fontSize={'xs'}
                color="fg.muted"
              >
                {t('filter.more')}
              </Text>
            )}
          </Flex>

          <ResultsByGroup searchResults={take(3, groupsData)} />
        </Box>
      )}
      {!isEmpty(globalGroupsData) && (
        <Box>
          <Flex alignItems={'center'} justifyContent={'space-between'}>
            <Text fontSize="xs" color="fg.muted" fontWeight="medium" p="2.5">
              {t('filter.groupsNotInContacts')}
            </Text>
            {gt(size(globalGroupsData), 3) && (
              <Text
                cursor="pointer"
                onClick={() => {
                  setActive(SearchTabs.Groups);
                }}
                fontSize={'xs'}
                color="fg.muted"
              >
                {t('filter.more')}
              </Text>
            )}
          </Flex>
          <ResultsByGroup searchResults={take(3, globalGroupsData)} />
        </Box>
      )}
    </>
  );
}
